import { calculateFunctions, getFunctionsObject, updateWidgetPropertyByPathUtils } from './utils';
import {
    FunctionEventTypeEnum,
    LayoutEnum,
    TBottomSheetCommonData,
    TButtonData,
    TCommonData,
    TInitWidgetState,
    TModalWindow,
    TMutableWidgetsData,
    TMutableWidgetsOption,
    TPage,
    TPayloadWidget,
    TSystemActions,
    TWAction,
    TWidget,
} from '@libs/types';

/**
 * Calculates the final enabled/disabled status based on a set of optional input properties.
 *
 * It ensures the returned 'enabled' and 'isDisabled' values are always opposites.
 * The logic prioritizes 'hasEnabledFunction' first, then 'hasIsDisabledFunction'.
 * If neither is present, it falls back to the 'enabled' or 'isDisabled' properties directly.
 *
 * @param {boolean} [enabled] - The proposed enabled state.
 * @param {boolean} [isDisabled] - The proposed disabled state.
 * @param {boolean} [hasEnabledFunction] - If true, the `enabled` property is the source of truth.
 * @param {boolean} [hasIsDisabledFunction] - If true, the `isDisabled` property is the source of truth.
 * @returns {{enabled: boolean, isDisabled: boolean}} An object with definitive enabled and disabled states.
 */
export const getStatus = (
    enabled?: boolean,
    isDisabled?: boolean,
    hasEnabledFunction?: boolean,
    hasIsDisabledFunction?: boolean,
): { enabled: boolean; isDisabled: boolean } => {
    if (hasEnabledFunction) {
        const finalEnabled = enabled ?? !isDisabled;
        return {
            enabled: finalEnabled,
            isDisabled: !finalEnabled,
        };
    }

    if (hasIsDisabledFunction) {
        const finalIsDisabled = isDisabled ?? !enabled;
        return {
            enabled: !finalIsDisabled,
            isDisabled: finalIsDisabled,
        };
    }

    if (typeof isDisabled === 'boolean') {
        return {
            enabled: !isDisabled,
            isDisabled: isDisabled,
        };
    } else if (typeof enabled === 'boolean') {
        return {
            enabled: enabled,
            isDisabled: !enabled,
        };
    }

    return {
        enabled: true,
        isDisabled: false,
    };
};

export const getWidgets = (
    widgets: TPayloadWidget<TMutableWidgetsData & TButtonData, TMutableWidgetsOption>[],
    result: TInitWidgetState = {
        mutableWidgetsMap: {},
        bottomSheetMap: {},
        modalWindowMap: {},
        internalIdWidgets: {},
        systemActions: {} as TSystemActions,
        rootWidgets: [],
        functions: {},
    },
) => {
    if (result.rootWidgets?.length === 0) {
        // Guard against null/missing widgets: the server may send `null` instead of a widget object,
        // so we skip it here to avoid dereferencing `null.uid`.
        widgets.forEach((widget) => widget && result.rootWidgets?.push(widget.uid));
    }
    for (const widget of widgets) {
        // Guard against null/missing widgets: a `null` element (server sent null instead of a widget)
        // would throw on `widget.uid` below, so we skip it and keep parsing the rest of the list.
        if (!widget) continue;
        const { isDisabled } = getStatus(
            widget?.options?.enabled,
            widget?.options?.isDisabled,
            !!widget?.functions?.[FunctionEventTypeEnum.ENABLED],
            !!widget?.functions?.[FunctionEventTypeEnum.DISABLED],
        );
        const widgetCopy = {
            ...widget,
            options: {
                ...widget?.options,
                isHide: widget?.options?.isHide ?? false,
                isDisabled: isDisabled,
            },
            isValid: true,
            systemMetadata: {},
        } as TWidget<TCommonData>;
        if (Array.isArray(widget?.widgets) && widget?.widgets?.length > 0) {
            // Guard against null/missing child widgets: drop any `null` entries so we never store an
            // `undefined` uid (which would later render an empty <Widget uid={undefined} />).
            widgetCopy.widgets = widget?.widgets.filter(Boolean).map((widget) => widget.uid);
        } else {
            widgetCopy.widgets = [];
        }
        result.mutableWidgetsMap[widget.uid] = widgetCopy;
        if (widget.internalId) {
            result.internalIdWidgets[widget.internalId] = widget.uid;
        }
        if (widget?.data?.default === true && !result.submitWidget) {
            result.submitWidget = widget;
        } else if (widget.internalId && (widget?.alternativeId === LayoutEnum.BOTTOM_SHEET || widget?.id === LayoutEnum.BOTTOM_SHEET)) {
            result.bottomSheetMap[widget.internalId] = getModalWindow(widget);
        } else if (widget.internalId && (widget?.alternativeId === LayoutEnum.MODAL_WINDOW || widget?.id === LayoutEnum.MODAL_WINDOW)) {
            result.modalWindowMap[widget.internalId] = getModalWindow(widget);
        }
        if (Array.isArray(widgetCopy.actions)) {
            const actionWithSystemActionType = widgetCopy.actions.find(
                (action: TWAction) => Array.isArray(action?.systemActionType) && action?.systemActionType?.length > 0,
            );
            if (actionWithSystemActionType) {
                actionWithSystemActionType.systemActionType?.forEach((sat) => {
                    if (!result.systemActions[sat]) {
                        result.systemActions[sat] = { action: actionWithSystemActionType, widget: widgetCopy };
                    }
                });
            }
        }
        if (Array.isArray(widget?.widgets) && widget.widgets.length > 0) {
            getWidgets(widget.widgets, result);
        }
        if (result.functions && widget.functions) {
            result.functions[widget.uid] = widget.functions;
        }
    }
    return result;
};

export const getModalWindow = (widget: TPayloadWidget<TCommonData>): TModalWindow => {
    return {
        // Guard against null/missing child widgets: drop any `null` entries (server sent null instead of a
        // widget) so the uid list never contains `undefined` (which would render an empty <Widget uid={undefined} />).
        uid: widget.widgets?.filter(Boolean).map((child) => child.uid),
        isOpen: !!(widget.data as TBottomSheetCommonData)?.isOpen,
        options: widget?.options,
    };
};

export const initWidgetState = (pages: TPage[]) => {
    try {
        if (pages && Array.isArray(pages) && pages.length > 0) {
            let widgets: TInitWidgetState = {
                mutableWidgetsMap: {},
                bottomSheetMap: {},
                modalWindowMap: {},
                internalIdWidgets: {},
                systemActions: {} as TSystemActions,
                rootWidgets: [],
                functions: {},
            };
            for (const page of pages) {
                widgets = getWidgets(page.widgets) || ({} as TInitWidgetState);
            }

            if (widgets.functions && Object.keys(widgets.functions).length > 0) {
                Object.keys(widgets.functions).forEach((uid) => {
                    // @ts-expect-error
                    const functions = widgets.functions[uid];
                    const widget = widgets.mutableWidgetsMap[uid];
                    const { internalIds, functionObjects } = getFunctionsObject(functions);

                    const mutableInternalIdWidgets: Record<string, TWidget<TMutableWidgetsData>> = {};
                    for (const internalId of internalIds) {
                        const uid = widgets.internalIdWidgets?.[internalId];
                        if (uid) {
                            mutableInternalIdWidgets[internalId] = widgets.mutableWidgetsMap?.[uid];
                        }
                    }

                    calculateFunctions(widget, functionObjects, mutableInternalIdWidgets, updateWidgetPropertyByPathUtils);
                });
            }
            return widgets;
        }
        return {};
    } catch (e) {
        console.error(e);
        return {};
    }
};
